import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Footer from "../Footer/footer";
import Navbar from "../Navbar/navbar";
import LogOutPopup from "./modals/logOutPopup";
import TrackOrderPopup from "./modals/trackOrderPopup";
import AddressesSection from "./addressesSection";
import YourOrders from "./yourOrders";
import CouponsSection from "./couponsSection";
import { addNewAddress, setShippingAddress, fetchAddresses } from "../../store/addressSlice";
import OtpPopup from "./modals/otpPopup";
import FooterDoodles from "../Footer/footerDoodles";
import icons from "../../icons.config";

const ProfilePage = () => {
  const navigate = useNavigate();
  const { section } = useParams();
  const dispatch = useDispatch();

  const orders = useSelector((state) => state.orders.items || []);
  const { shippingAddress, savedAddresses, loading, error } = useSelector((state) => state.address);
  // const authToken = localStorage.getItem("authToken");

  const [emailVerified, setEmailVerified] = useState(false);
  const [showLogoutPopup, setShowLogoutPopup] = useState(false);
  const [showTrackOrderPopup, setShowTrackOrderPopup] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [isEditable, setIsEditable] = useState(false);

   // ✅ Get auth state from Redux (instead of localStorage)
   const authToken = useSelector((state) => state.auth.authToken);
 
 
   
 
   useEffect(() => {
     if (authToken) {
       dispatch(fetchAddresses()); // ✅ Fetch addresses when logged in
     } else {
       navigate("/login"); // ✅ Redirect if not authenticated
     }
   }, [authToken, dispatch, navigate]);

  // Section Mapping
  const sectionMapping = {
    undefined: "YOUR ORDERS",
    addresses: "Addresses",
    "coupons-offers": "Coupons & Offers",
  };

  const [activeSection, setActiveSection] = useState(sectionMapping[section] || "YOUR ORDERS");

  // Update URL based on active section
  useEffect(() => {
    const newPath =
      activeSection === "YOUR ORDERS"
        ? "/profile"
        : activeSection === "Addresses"
        ? "/profile/addresses"
        : "/profile/coupons-offers";
    navigate(newPath, { replace: true });
  }, [activeSection, navigate]);

  const user = useSelector((state) => state.auth.user.user); // ✅ Get user from Redux

  // Debugging logs
  console.log("User from Redux:", user);
  console.log("User Email:", user?.user?.email_id);
  console.log("User First Name:", user?.first_name);
  console.log("User Last Name:", user?.last_name);

  const [address, setAddress] = useState(() => {
    // Try to get the address from localStorage first
    const storedAddress = JSON.parse(localStorage.getItem("userAddress"));
    
    return storedAddress || {
      firstName: shippingAddress?.firstName || user?.first_name || "",
      lastName: shippingAddress?.last_name || user?.last_name || "",
      email: shippingAddress?.email || user?.email_id || "",
      phone: shippingAddress?.phone || user?.mobile_number || "",
      address: shippingAddress?.address || "",
    };
  });
  
  // ✅ Store the first address in localStorage
  useEffect(() => {
    if (!localStorage.getItem("userAddress") && (shippingAddress || user)) {
      const newAddress = {
        firstName: shippingAddress?.firstName || user?.first_name || "",
        lastName: shippingAddress?.lastName || user?.last_name || "",
        email:  user?.email_id || "",
        phone: user?.mobile_number || "",
        address: shippingAddress?.address || "",
      };
  
      // ✅ Save to localStorage
      localStorage.setItem("userAddress", JSON.stringify(newAddress));
      
      // ✅ Update state to match the stored address
      setAddress(newAddress);
    }

    console.log("Updated Address State:", address);
  }, [shippingAddress, user]); // ✅ Dependency array includes `user`

  // Handle Form Input Changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAddress((prev) => ({ ...prev, [name]: value }));
  };

  // Handle Profile Update
  const handleFormSubmit = (e) => {
    e.preventDefault();
    dispatch(addNewAddress({ ...address }));
    dispatch(setShippingAddress(address));
    setIsEditable(false);
    alert("Profile updated successfully!");
  };

  // Full Address Formatting
  const fullAddress = shippingAddress
    ? `${shippingAddress.house_number || ""}, ${shippingAddress.main_street || ""},${shippingAddress.landmark || ""}, ${shippingAddress.city || ""}, ${shippingAddress.state || ""} - ${shippingAddress.postal_code || ""}, ${shippingAddress.country || ""}`
    : "";

  // Handle OTP Verification
  const handleVerifyClick = () => {
    setShowOtpModal(true);
  };

  return (
    <>
      <Navbar />

      <div
        className={`flex flex-col lg:flex-row gap-10 lg:gap-0 bg-tertiary relative top-16 lg:top-20 py-24 px-11 ${
          activeSection === "Coupons & Offers" ? "lg:pl-20 xl:pl-32 lg:pr-9 xl:pr-0" : "lg:px-20 xl:px-32"
        } overflow-hidden ${
          showLogoutPopup || showTrackOrderPopup || showOtpModal
            ? "opacity-30 pointer-events-none backdrop-blur-sm"
            : "opacity-100"
        }`}
      >
        {/* Vertical Line */}
        <div className="hidden lg:block w-[2px] bg-secondary absolute left-1/3 top-0 bottom-0 mx-9"></div>

        {/* Left Section */}
        <div className="lg:w-1/3 w-full bg-tertiary px-1">
          <h2 className="text-4xl font-outfit font-light mb-4 text-primary1">YOUR PROFILE</h2>
          <p className="font-outfit font-light text-2xl">{address.firstName} {address.lastName}</p>
          <p className="text-hcolor font-outfit font-light">{address.email}</p>
          {!emailVerified && (
            <button onClick={handleVerifyClick} className="text-sm underline">Verify your E-mail</button>
          )}
          <p className="text-hcolor font-outfit font-light">{address.phone}</p>
          <p className="text-hcolor font-outfit font-light">{fullAddress}</p>

          <div className="mt-6">
            {["Addresses", "Track Order", "Coupons & Offers", "Logout"].map((link, index) => (
              <p
                key={index}
                className="text-primary1 underline mb-2 cursor-pointer font-outfit font-light"
                onClick={() => {
                  if (link === "Logout") {
                    setShowLogoutPopup(true);
                  } else if (link === "Track Order") {
                    setShowTrackOrderPopup(true);
                  } else {
                    setActiveSection(link);
                  }
                }}
              >
                {link}
              </p>
            ))}
          </div>
        </div>

        {/* Right Section */}
        <div className="lg:w-2/3 w-full bg-tertiary">
          <div className="flex items-center mb-4">
            {activeSection !== "YOUR ORDERS" && (
              <button onClick={() => setActiveSection("YOUR ORDERS")} className="text-primary1 font-outfit font-light flex items-center">
                <img src={icons.back} alt="Back" className="w-9 h-8" />
              </button>
            )}
            <h2 className="text-3xl font-light font-outfit lg:ml-7">{activeSection}</h2>
          </div>

          <div className="w-full text-hcolor h-[85%]">
            {activeSection === "YOUR ORDERS" ? (
              <YourOrders orders={orders} />
            ) : activeSection === "Addresses" ? (
              <AddressesSection
                handleInputChange={handleInputChange}
                handleFormSubmit={handleFormSubmit}
                address={address}
                isEditable={isEditable}
              />
            ) : activeSection === "Coupons & Offers" ? (
              <CouponsSection />
            ) : (
              <p>Content for {activeSection}</p>
            )}
          </div>
        </div>
      </div>

      {showLogoutPopup && <LogOutPopup setShowLogoutPopup={setShowLogoutPopup} />}
      {showTrackOrderPopup && <TrackOrderPopup onClose={() => setShowTrackOrderPopup(false)} />}
      {showOtpModal && <OtpPopup show={showOtpModal} onClose={() => setShowOtpModal(false)} onVerify={setEmailVerified} />}
      <FooterDoodles />
      <Footer />
    </>
  );
};

export default ProfilePage;
